import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmployeeAvatar } from "@/components/shared/EmployeeAvatar";
import { api, errMsg } from "@/lib/api";
import { Pick } from "@/pages/gerencia/Sales";
import { SignaturePad } from "@/pages/prevencion/SignaturePad";
import { beep, Card, OtSelect, savedOt, ScanBox, Screen, useOts } from "./common";
import { cachedGet, cacheRoster, findWorker, normRut, queuedFor, send, useQueue } from "./offline";

const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "America/Santiago" });
const KINDS = [{ id: "charla_5min", name: "Charla 5 minutos" }, { id: "odi", name: "ODI" }, { id: "capacitacion", name: "Capacitación" }, { id: "induccion", name: "Inducción" }];

const NewTalk = ({ ot, onCreated }) => {
  const [f, setF] = useState({ kind: "charla_5min", topic: "", speaker: "" });
  const save = async () => {
    try { const { data } = await api.post("/prevencion/talks", { ...f, ot_id: ot, content: "", date: today(), duration_min: 10 }); toast.success(`${data.code} creada`); onCreated(data.id); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card className="space-y-3">
      <div className="font-extrabold">Nueva charla</div>
      <Pick testid="terreno-talk-kind" value={f.kind} onChange={(v) => setF({ ...f, kind: v })} options={KINDS} />
      <Input data-testid="terreno-talk-topic" placeholder="Tema" value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} />
      <Input data-testid="terreno-talk-speaker" placeholder="Relator" value={f.speaker} onChange={(e) => setF({ ...f, speaker: e.target.value })} />
      <Button data-testid="terreno-talk-create" className="w-full rounded-full" disabled={f.topic.trim().length < 3 || f.speaker.trim().length < 2} onClick={save}>Crear y comenzar firmas</Button>
    </Card>
  );
};

const Signing = ({ talk, onDone, queuedIds }) => {
  const [qr, setQr] = useState(null);
  const [who, setWho] = useState(null);
  const [sig, setSig] = useState(null);
  const [key, setKey] = useState(0);
  const reset = () => { setQr(null); setWho(null); setSig(null); setKey((k) => k + 1); };
  const identify = async (t) => {
    try {
      const { data } = await api.post(`/prevencion/talks/${talk.id}/identify`, { qr: t });
      if (data.already) { beep(false); return toast.info(`${data.name} ya firmó esta charla`); }
      beep(true); setQr(t); setWho(data);
    } catch (e) {
      if (e.response) { beep(false); toast.error(errMsg(e)); return; }
      const w = findWorker(talk.ot_id, t);
      if (w && (talk.attendees.some((a) => a.employee_id === w.id) || queuedIds.includes(w.id))) { beep(false); return toast.info(`${w.name} ya firmó esta charla`); }
      beep(true); setQr(t); setWho(w || { id: null, name: `RUT ${normRut(t)}`, rut: normRut(t), position: "Sin señal" });
    }
  };
  const sign = async () => {
    try {
      const r = await send(`/prevencion/talks/${talk.id}/attendees`, { qr, signature: sig }, `Firma ${talk.code} ${who.name}`, { name: who.name, employee_id: who.id });
      if (r.queued) { beep(true); toast.success(`${who.name}: firma guardada sin señal`); reset(); return; }
      const data = r.data;
      beep(true); data.result === "registrado" ? toast.success(`${data.name} firmó`) : toast.info(`${data.name} ya estaba registrado`);
      reset(); onDone();
    } catch (e) { beep(false); if (!e.quota) toast.error(errMsg(e)); }
  };
  if (!who) return <><div className="text-sm font-semibold text-[#258F00]">1. El trabajador escanea su carnet</div><ScanBox onRead={identify} placeholder="O escriba el RUT y presione Ir" testid="terreno-sign-scan" /></>;
  return (
    <Card className="space-y-3">
      <div className="flex items-center gap-3" data-testid="terreno-sign-worker">
        {who.id && <EmployeeAvatar id={who.id} v={who.photo_v} name={who.name} size={52} />}
        <div className="min-w-0"><div className="font-extrabold truncate">{who.name}</div><div className="text-xs text-[#5b6656]">{who.rut} · {who.position}</div></div>
      </div>
      <div className="text-sm font-semibold text-[#258F00]">2. El trabajador firma con el dedo</div>
      <SignaturePad key={key} onChange={setSig} />
      <div className="flex gap-2"><Button data-testid="terreno-sign-cancel" variant="outline" className="rounded-full" onClick={reset}>Cancelar</Button>
        <Button data-testid="terreno-sign-confirm" className="flex-1 rounded-full h-12 text-base font-extrabold" disabled={!sig} onClick={sign}>Confirmar firma</Button></div>
    </Card>
  );
};

export default function Charlas() {
  const lk = useOts("/prevencion/lookups", (d) => d.ots);
  const [ot, setOt] = useState(savedOt());
  const [list, setList] = useState([]);
  const [talkId, setTalkId] = useState(null);
  const [talk, setTalk] = useState(null);
  const [creating, setCreating] = useState(false);
  const q = useQueue();
  const queued = talkId ? queuedFor(`/prevencion/talks/${talkId}/attendees`).map((x) => ({ id: x.meta?.employee_id, name: x.meta?.name || x.label })) : [];
  const loadList = useCallback(() => ot && cachedGet("/prevencion/talks", { ot_id: ot }).then((d) => setList(d.filter((t) => t.status !== "cerrada"))).catch(() => {}), [ot]);
  const loadTalk = useCallback(() => talkId && cachedGet(`/prevencion/talks/${talkId}`).then(setTalk).catch(() => {}), [talkId]);
  useEffect(() => { cacheRoster(ot); }, [ot]);
  useEffect(() => { if (q.online) loadTalk(); }, [q.pending, q.online, loadTalk]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadTalk(); }, [loadTalk]);
  const close = async () => { try { await api.post(`/prevencion/talks/${talkId}/close`); toast.success("Charla cerrada"); setTalkId(null); setTalk(null); loadList(); } catch (e) { toast.error(errMsg(e)); } };
  if (talk) return (
    <Screen title={talk.code} testid="terreno-talk">
      <Card><div className="font-extrabold">{talk.topic}</div><div className="text-xs text-[#5b6656]">{talk.ot_code} · {talk.speaker}</div></Card>
      <Signing talk={talk} onDone={loadTalk} queuedIds={queued.map((w) => w.id).filter(Boolean)} />
      <Card><div className="text-xs font-bold uppercase tracking-wider text-[#5b6656] mb-1" data-testid="terreno-talk-count">Firmaron ({talk.attendees.length + queued.length})</div>
        {talk.attendees.map((a) => <div key={a.employee_id} className="py-1.5 text-sm border-b border-[#f0f2ee] last:border-0">{a.name}{a.signature ? " ✓" : ""}</div>)}
        {queued.map((w, i) => <div key={`q${i}`} data-testid={`terreno-talk-queued-${i}`} className="py-1.5 text-sm border-b border-[#f0f2ee] last:border-0 text-[#8a5a00]">{w.name} · pendiente de envío</div>)}</Card>
      <div className="flex gap-2"><Button data-testid="terreno-talk-back" variant="outline" className="rounded-full" onClick={() => { setTalkId(null); setTalk(null); loadList(); }}>Volver</Button>
        <Button data-testid="terreno-talk-close" className="flex-1 rounded-full" onClick={close}>Cerrar charla</Button></div>
    </Screen>);
  return (
    <Screen title="Firmas de prevención" testid="terreno-talks">
      <OtSelect testid="terreno-talks-ot" ots={lk} value={ot} onChange={setOt} />
      {ot && !creating && <Button data-testid="terreno-talk-new" className="w-full h-12 rounded-2xl" onClick={() => setCreating(true)}><Plus size={18} className="mr-1" />Nueva charla</Button>}
      {ot && creating && <NewTalk ot={ot} onCreated={(id) => { setCreating(false); setTalkId(id); }} />}
      {list.map((t) => (
        <button key={t.id} data-testid={`terreno-talk-item-${t.id}`} onClick={() => setTalkId(t.id)} className="w-full text-left rounded-2xl bg-white border border-[#e4e8e1] p-4 active:scale-[0.98]">
          <div className="font-semibold">{t.topic}</div><div className="text-xs text-[#5b6656]">{t.code} · {t.date} · {t.attendees_count} firmas</div>
        </button>))}
      {ot && list.length === 0 && !creating && <div className="text-center text-sm text-[#5b6656]">No hay charlas abiertas en esta OT.</div>}
    </Screen>
  );
}
